import React, { useState } from 'react';
import './BulkEditModal.css';
import { API_URL } from '../config';

interface BulkEditModalProps {
  roadId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkEditModal: React.FC<BulkEditModalProps> = ({ roadId, onClose, onSuccess }) => {
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!field || value === '') return;
    
    setLoading(true);
    try {
      const updates: any = {};
      
      // Convert value to appropriate type
      if (['longueur', 'largeur', 'deflection_t1', 'deflection_t2', 'deflection_t3', 'deflection_t4', 'deflection_t5'].includes(field)) {
        updates[field] = parseFloat(value);
      } else if (['surface_glissante', 'degradation_revetement', 'fissures_longitudinales', 'fissures_transversales', 'nids_poule', 'deformation'].includes(field)) {
        updates[field] = parseInt(value);
      } else {
        updates[field] = value;
      }
      
      const response = await fetch(`${API_URL}/roads/${roadId}/sections/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error bulk updating:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>✏️ Modification en Masse</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <p>Appliquer la même valeur à tous les tronçons de cette route</p>
          
          <div className="form-group">
            <label>Champ à modifier</label>
            <select value={field} onChange={(e) => setField(e.target.value)}>
              <option value="">Sélectionner...</option>
              <optgroup label="Dimensions">
                <option value="largeur">Largeur</option>
              </optgroup>
              <optgroup label="Classification">
                <option value="trafic">Classe de trafic</option>
                <option value="structure">Type de structure</option>
              </optgroup>
              <optgroup label="Indicateurs Visuels">
                <option value="surface_glissante">Surface glissante</option>
                <option value="degradation_revetement">Dégradation revêtement</option>
                <option value="fissures_longitudinales">Fissures longitudinales</option>
                <option value="fissures_transversales">Fissures transversales</option>
                <option value="nids_poule">Nids de poule</option>
                <option value="deformation">Déformation</option>
              </optgroup>
            </select>
          </div>
          
          {field && (
            <div className="form-group">
              <label>Nouvelle valeur</label>
              {field === 'trafic' ? (
                <select value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="">Sélectionner...</option>
                  <option value="T0">T0 - Très faible</option>
                  <option value="T1">T1 - Faible</option>
                  <option value="T2">T2 - Moyen</option>
                  <option value="T3">T3 - Fort</option>
                </select>
              ) : field === 'structure' ? (
                <select value={value} onChange={(e) => setValue(e.target.value)}>
                  <option value="">Sélectionner...</option>
                  <option value="E">Enrobé (E)</option>
                  <option value="B">Béton (B)</option>
                  <option value="S">Souple (S)</option>
                  <option value="M">Mixte (M)</option>
                </select>
              ) : ['surface_glissante', 'degradation_revetement', 'fissures_longitudinales', 'fissures_transversales', 'nids_poule', 'deformation'].includes(field) ? (
                <input
                  type="range"
                  min="0"
                  max="5"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              ) : (
                <input
                  type="number"
                  step="0.1"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              )}
              {value && <div className="value-display">Valeur: {value}</div>}
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Annuler</button>
          <button 
            className="btn-apply" 
            onClick={handleSubmit}
            disabled={!field || value === '' || loading}
          >
            {loading ? '⏳ Application...' : '✅ Appliquer à tous'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModal;
